import {
    getObjectSchema,
    OBJECT_PROP_SKIP,
    toWireKey,
} from '../Hasp/properties/v2/wireFormat.js';
import { getEditorForPropertyDefinition } from '../automation/schema/PropertyEditorMeta.js';
import { isSchemaPropertyVisible } from '../Hasp/properties/v2/propertyVisibility.js';
import { getGaugeNeedleActionProperties } from '../Hasp/gaugeNeedles.js';

const SCALAR_TYPES = new Set(['string', 'color', 'int', 'float', 'number', 'bool', 'enum', 'resource']);

export function isRuntimeSettableProperty(reference, definition, schema, partDefinition) {
    if (!schema || !definition || definition.internal === true || definition.exportSpecial === true) return false;
    if (!SCALAR_TYPES.has(definition.type)) return false;
    if (reference.part === null && OBJECT_PROP_SKIP.has(reference.name)) return false;
    const wireKey = reference.part
        ? toWireKey(reference.part, reference.name, partDefinition)
        : toWireKey('main', reference.name, {
            exportSuffix: '',
            properties: { [reference.name]: definition },
        });
    return typeof wireKey === 'string' && wireKey.length > 0;
}

export function getActionSettableProperties(targetNode) {
    const schema = targetNode?.schemaV2 ?? getObjectSchema(targetNode?.obj);
    if (!schema) return [];
    const descriptors = [];
    for (const [name, definition] of Object.entries(schema.properties ?? {})) {
        // Match the Data properties UI: a conditional property remains in the
        // model, but is offered as an action target only while its condition is
        // true. Existing actions are therefore preserved across type changes,
        // while new actions cannot select start_value on a non-Range target.
        const visibilityNode = targetNode?.schemaV2 ? targetNode : { ...targetNode, schemaV2: schema };
        if (isSchemaPropertyVisible(visibilityNode, name) &&
            isRuntimeSettableProperty({ part: null, name }, definition, schema)) {
            descriptors.push(descriptor(schema, null, name, definition));
        }
    }
    for (const [part, partDefinition] of Object.entries(schema.parts ?? {})) {
        for (const [name, definition] of Object.entries(partDefinition.properties ?? {})) {
            if (isRuntimeSettableProperty({ part, name }, definition, schema, partDefinition)) {
                descriptors.push(descriptor(schema, part, name, definition, partDefinition));
            }
        }
    }
    // Gauge needles are an ordered, Designer-owned collection rather than
    // schema parts. Expose their scalar leaves through the same descriptor
    // contract so the action editor, codec and serializer remain generic.
    if (targetNode?.obj === 'gauge') descriptors.push(...getGaugeNeedleActionProperties(targetNode));
    return descriptors;
}

function descriptor(schema, part, name, metadata, partDefinition) {
    return {
        key: `${part ?? 'object'}:${name}`,
        property: { part, name },
        label: metadata.label ?? ({ val: 'Value', start_value: 'Start value' }[name] ??
            name.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase())),
        type: metadata.type,
        editor: getEditorForPropertyDefinition(metadata),
        metadata,
        wireKey: part ? toWireKey(part, name, partDefinition) : name,
        default: part ? schema.defaults?.styles?.[part]?.[name] : schema.defaults?.properties?.[name],
    };
}

export function resolveActionProperty(targetNode, reference) {
    const descriptors = getActionSettableProperties(targetNode);
    // A Designer-authored reference can carry the non-wire needle identity.
    // Resolve it by identity after reorder; the descriptor supplies the new
    // current index/wire key. Parsed firmware commands have no identity and
    // continue to resolve by their explicit runtime index below.
    if (reference?.needleId) {
        const byNeedleId = descriptors.find(item => item.property.needleId === reference.needleId &&
            item.property.name.endsWith(`.${reference.name?.split('.').pop()}`));
        if (byNeedleId) return byNeedleId;
        if (String(reference.name ?? '').startsWith('needles[')) return null;
    }
    return descriptors.find(item =>
        item.property.part === (reference?.part ?? null) && item.property.name === reference?.name
    ) ?? null;
}

export function resolveActionPropertyByWireKey(targetNode, wireKey) {
    const matches = getActionSettableProperties(targetNode).filter(item => item.wireKey === wireKey);
    return matches.length === 1 ? matches[0] : null;
}

export function isActionTarget(targetNode) {
    return getActionSettableProperties(targetNode).length > 0;
}

/**
 * Return the objects that can be targeted by an event action on sourceNode.
 * The source itself is included: a control can update its own display value
 * when its event fires (for example, arc.changed -> arc.value_str={val}).
 */
/**
 * The container whose objects an action of `sourceNode` can target: the
 * source's parent, or the page itself when the page is the event source.
 */
export function getActionScope(sourceNode) {
    return sourceNode?.obj === 'page' || sourceNode?.obj === 'template_page'
        ? sourceNode
        : sourceNode?.getParent?.();
}

export function getActionTargetNodes(sourceNode) {
    return (getActionScope(sourceNode)?.getChildren?.() ?? [])
        .filter(item => isActionTarget(item) && Number.isInteger(Number(item.haspid)));
}
